import React, { ReactElement, useCallback, useMemo, useState } from "react";
import { ResourceTagFormDrawerProps } from "./ResourceTagFormDrawer.types";
import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
} from "src/components/Drawer";
import { ResourceTagForm } from "../ResourceTagForm";
import { localize } from "src/l10n";
import { ResourceTagModel, createResourceTagAsync, updateResourceTagAsync } from "src/api";
import { UpdateResourceTagModelHandler } from "../types";
import { UnstyledButton } from "src/ui";
import { SmallBodyBolder } from "src/components/Text";
import { Conditional } from "src/components/Conditional";
import { DefaultButton, PrimaryButton } from "@fluentui/react";
import { useDispatch } from "react-redux";
import { resourceTagCreated, resourceTagUpdated } from "src/redux";

const initialModel: ResourceTagModel = {
    name: "",
    color: "",
} as const;

/**
 * ResourceTagFormDrawer component
 *
 * @param {ResourceTagFormDrawerProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function ResourceTagFormDrawer({
    onDismiss,
    open,
    tagId,
}: ResourceTagFormDrawerProps): ReactElement | null {
    const dispatch = useDispatch();
    const [model, setModel] = useState<ResourceTagModel>(initialModel);
    const [state, setState] = useState({
        isSaving: false,
    });
    const { isSaving } = state;
    const createMode = tagId === undefined;

    const l10n = useMemo(() => ({
        createNew: localize("SkapaNy"),
        edit: localize("Redigera"),
        cancel: localize("Avbryt"),
        save: localize("Spara"),
        saveAndContinue: localize("SparaOchFortsatt"),
    }), []);

    const onModelChanged = useCallback<UpdateResourceTagModelHandler>(
        (updater) => setModel((prevModel) => {
            const updatedModel = updater(prevModel);

            console.log("Updated model:", updatedModel);

            return updatedModel;
        }), [setModel]);

    const onOpenChange = useCallback(
        (isOpen: boolean) => {
            if (isOpen) {
                return;
            }

            onDismiss(false);
        }, [onDismiss]);
    
    const onCancelClicked = useCallback(() => onDismiss(false), [onDismiss]);

    const onSubmitting = useCallback(async (closeOnSuccess: boolean) => {
        if (isSaving) {
            return;
        }

        setState((prevState) => ({ ...prevState, isSaving: true }));

        let tag: Spintr.ResourceTag | undefined;
        try {
            tag = !tagId
                ? await createResourceTagAsync(model)
                : await updateResourceTagAsync(tagId, model);
        } catch (err) {
            console.error("Error saving resource tag:", err);

            setState((prevState) => ({ ...prevState, isSaving: false }));
            return;
        }

        setState((prevState) => ({ ...prevState, isSaving: false }));
        setModel(initialModel);
        dispatch(tagId
            ? resourceTagCreated(tag)
            : resourceTagUpdated(tag)
        );

        if (!closeOnSuccess) {
            return;
        }

        onDismiss(true);
    }, [
        isSaving,
        model,
        onDismiss,
        setModel,
        setState,
        tagId,
    ]);


    const submitAndCloseOnSuccess = useCallback(
        () => onSubmitting(true),
        [onSubmitting]);

    const submitAndKeepOpen = useCallback(
        () => onSubmitting(false),
        [onSubmitting]);

    return (
        <Drawer className="ResourceTagFormDrawer"
                onOpenChange={onOpenChange}
                open={open}
                position="end"
                size="medium">
            <DrawerHeader>
                <DrawerHeaderTitle>
                    {createMode ? l10n.createNew : l10n.edit}
                </DrawerHeaderTitle>
            </DrawerHeader>

            <DrawerBody>
                <ResourceTagForm model={model}
                                 onModelChange={onModelChanged} />
            </DrawerBody>

            <DrawerFooter className="ResourceTagFormDrawer-footer">
                <UnstyledButton disabled={isSaving}
                                onClick={onCancelClicked}>
                    <SmallBodyBolder color="contentLight">
                        {l10n.cancel}
                    </SmallBodyBolder>
                </UnstyledButton>
                <Conditional condition={createMode}>
                    <DefaultButton className="QualityHubTaskFormDrawer-saveAndClose"
                                   disabled={isSaving}
                                   onClick={submitAndCloseOnSuccess}
                                   type="submit">
                        {l10n.save}
                    </DefaultButton>
                </Conditional>
                <PrimaryButton className="QualityHubTaskFormDrawer-save"
                               disabled={isSaving}
                               onClick={createMode
                                    ? submitAndKeepOpen
                                    : submitAndCloseOnSuccess}
                               type="submit">
                    {createMode ? l10n.saveAndContinue : l10n.save}
                </PrimaryButton>
            </DrawerFooter>
        </Drawer>
    );
}

export default ResourceTagFormDrawer;

